import React, { useState, useRef, useEffect } from "react";
import { Box, Typography, keyframes, useTheme, useMediaQuery } from "@mui/material";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import MobileFriendlyIcon from "@mui/icons-material/MobileFriendly";
import SearchIcon from "@mui/icons-material/Search";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

// ============ ANIMATIONS ============
const pulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.05); opacity: 0.9; }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-6px); }
`;

const glow = keyframes`
  0%, 100% { box-shadow: 0 0 8px rgba(53, 103, 214, 0.4); }
  50% { box-shadow: 0 0 16px rgba(53, 103, 214, 0.8); }
`;

// ============ VIZ APP 3D CARD ============
const VizApp3DCard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  
  const cardRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  
  // Mouse position tracking for parallax
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  
  // Smooth spring animations for 3D tilt
  const rotateX = useSpring(useTransform(mouseY, [-0.5, 0.5], [8, -8]), { stiffness: 300, damping: 30 });
  const rotateY = useSpring(useTransform(mouseX, [-0.5, 0.5], [-8, 8]), { stiffness: 300, damping: 30 });
  
  // Depth transforms for parallax layers
  const backgroundZ = useTransform(mouseX, [-0.5, 0.5], [-10, 10]);
  const deviceZ = useTransform(mouseX, [-0.5, 0.5], [-5, 5]);
  const featuresZ = useTransform(mouseX, [-0.5, 0.5], [5, -5]);
  const badgesZ = useTransform(mouseX, [-0.5, 0.5], [8, -8]);
  
  // VizApp features based on the actual page - core features only
  const features = [
    { 
      icon: <CheckCircleIcon sx={{ fontSize: { xs: "12px", md: "14px" } }} />, 
      title: "Complete Coverage", 
      desc: "Hematology & Biochemistry",
      color: "#7BB58C"
    },
    { 
      icon: <SearchIcon sx={{ fontSize: { xs: "12px", md: "14px" } }} />, 
      title: "Interactive", 
      desc: "Body, Search & Navigation",
      color: "#3567D6"
    },
    { 
      icon: <MobileFriendlyIcon sx={{ fontSize: { xs: "12px", md: "14px" } }} />, 
      title: "Mobile-Friendly", 
      desc: "Secure & Flexible",
      color: "#3567D6"
    },
  ];
  
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!cardRef.current || isMobile) return;
      
      const rect = cardRef.current.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      
      const x = (e.clientX - centerX) / (rect.width / 2);
      const y = (e.clientY - centerY) / (rect.height / 2);
      
      mouseX.set(x);
      mouseY.set(y);
    };
    
    const handleMouseEnter = () => setIsHovered(true);
    const handleMouseLeave = () => {
      setIsHovered(false);
      mouseX.set(0);
      mouseY.set(0);
    };
    
    const card = cardRef.current;
    if (card) {
      card.addEventListener("mousemove", handleMouseMove);
      card.addEventListener("mouseenter", handleMouseEnter);
      card.addEventListener("mouseleave", handleMouseLeave);
    }
    
    return () => {
      if (card) {
        card.removeEventListener("mousemove", handleMouseMove);
        card.removeEventListener("mouseenter", handleMouseEnter);
        card.removeEventListener("mouseleave", handleMouseLeave);
      }
    };
  }, [isMobile, mouseX, mouseY]);
  
  return (
    <Box
      ref={cardRef}
      sx={{
        width: "100%",
        height: "100%",
        position: "relative",
        perspective: isMobile ? "800px" : "1200px",
        perspectiveOrigin: "center center",
        overflow: "hidden",
        backgroundColor: "#f8fafc",
        cursor: isMobile ? "default" : "pointer",
      }}
    >
      {/* Background Layer - Parallax */}
      <motion.div
        style={{
          position: "absolute",
          width: "100%",
          height: "100%",
          background: "linear-gradient(135deg, rgba(2, 38, 116, 0.08) 0%, rgba(53, 103, 214, 0.05) 100%)",
          translateZ: backgroundZ,
          willChange: "transform",
        }}
      />
      
      {/* Main Content Container */}
      <motion.div
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          padding: isMobile ? "12px" : "16px",
          gap: "8px",
          rotateX: isMobile ? 0 : rotateX,
          rotateY: isMobile ? 0 : rotateY,
          transformStyle: "preserve-3d",
          willChange: "transform",
        }}
        animate={{
          y: [0, -4, 0],
          scale: [1, 1.01, 1],
        }}
        transition={{
          duration: 4,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      >
        {/* Viz App Badge - Top Parallax Layer */}
        <motion.div
          style={{
            display: "flex",
            gap: "6px",
            translateZ: badgesZ,
            zIndex: 10,
            willChange: "transform",
          }}
        >
          <Box
            sx={{
              background: "linear-gradient(135deg, #022674, #3567D6)",
              borderRadius: "10px",
              padding: "4px 10px",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              boxShadow: "0 4px 12px rgba(2, 38, 116, 0.3)",
              animation: `${float} 3s ease-in-out infinite`,
            }}
          >
            <MobileFriendlyIcon sx={{ fontSize: "12px", color: "#fff" }} />
            <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "9px", fontWeight: 700, color: "#fff", textTransform: "uppercase" }}>
              Viz App
            </Typography>
          </Box>
        </motion.div>
        
        {/* Mobile Device Preview - Middle Parallax Layer */}
        <motion.div
          style={{
            flex: 1,
            position: "relative",
            translateZ: deviceZ,
            willChange: "transform",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {/* Device Frame */}
          <Box
            sx={{
              width: "100%",
              maxWidth: { xs: "100px", md: "140px" },
              height: { xs: "160px", md: "220px" },
              background: "#022674",
              borderRadius: { xs: "12px", md: "16px" },
              padding: { xs: "6px", md: "8px" },
              boxShadow: "0 8px 24px rgba(2, 38, 116, 0.4)",
              position: "relative",
              overflow: "hidden",
            }}
          >
            {/* Device Screen */}
            <Box
              sx={{
                width: "100%",
                height: "100%",
                background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
                borderRadius: { xs: "8px", md: "10px" },
                padding: { xs: "6px", md: "8px" },
                display: "flex",
                flexDirection: "column",
                gap: { xs: "4px", md: "6px" },
                overflow: "hidden",
              }}
            >
              {/* Search Bar */}
              <Box
                sx={{
                  padding: { xs: "3px 6px", md: "4px 8px" },
                  background: "#f1f5f9",
                  borderRadius: { xs: "4px", md: "6px" },
                  display: "flex",
                  alignItems: "center",
                  gap: { xs: "3px", md: "4px" },
                }}
              >
                <SearchIcon sx={{ fontSize: { xs: "8px", md: "10px" }, color: "#64748b" }} />
                <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: { xs: "6px", md: "7px" }, color: "#94a3b8" }}>
                  {isMobile ? "Search..." : "Search parameters..."}
                </Typography>
              </Box>
              
              {/* Health Data Cards */}
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: { xs: "3px", md: "4px" },
                  overflowY: "auto",
                  "&::-webkit-scrollbar": { display: "none" },
                  scrollbarWidth: "none",
                }}
              >
                {/* Test Parameter Cards */}
                {[
                  { name: "Glucose", value: "92", unit: "mg/dL", status: "normal", color: "#10b981" },
                  { name: "Hb", value: "14.2", unit: "g/dL", status: "normal", color: "#10b981" },
                  { name: "WBC", value: "6.8", unit: "×10³", status: "normal", color: "#10b981" },
                ].map((param, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.1, duration: 0.3 }}
                  >
                    <Box
                      sx={{
                        padding: { xs: "3px 4px", md: "4px 6px" },
                        background: "#ffffff",
                        borderRadius: { xs: "4px", md: "5px" },
                        border: `1px solid ${param.color}30`,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: { xs: "6px", md: "7px" }, fontWeight: 600, color: "#1e293b", lineHeight: 1.2 }}>
                          {param.name}
                        </Typography>
                        <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: { xs: "5px", md: "6px" }, color: "#64748b", lineHeight: 1.2 }}>
                          {param.value}{param.unit}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          width: { xs: "4px", md: "5px" },
                          height: { xs: "4px", md: "5px" },
                          borderRadius: "50%",
                          background: param.color,
                          boxShadow: `0 0 4px ${param.color}40`,
                        }}
                      />
                    </Box>
                  </motion.div>
                ))}
              </Box>
              
              {/* Feature Icons Row */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-around",
                  gap: { xs: "2px", md: "4px" },
                }}
              >
                {features.map((feature, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      width: { xs: "18px", md: "24px" },
                      height: { xs: "18px", md: "24px" },
                      borderRadius: { xs: "4px", md: "6px" },
                      background: `linear-gradient(135deg, ${feature.color}20, ${feature.color}10)`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: `1px solid ${feature.color}30`,
                    }}
                  >
                    <Box sx={{ color: feature.color }}>
                      {feature.icon}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </motion.div>
        
        {/* Features List - Bottom Parallax Layer */}
        <motion.div
          style={{
            translateZ: featuresZ,
            willChange: "transform",
          }}
        >
          <Box
            sx={{
              display: "flex",
              gap: { xs: "4px", md: "6px" },
              justifyContent: "space-between",
            }}
          >
            {features.map((feature, idx) => (
              <Box
                key={idx}
                sx={{
                  flex: "0 0 30%",
                  padding: { xs: "4px 6px", md: "6px 8px" },
                  background: "linear-gradient(135deg, rgba(53, 103, 214, 0.08) 0%, rgba(123, 181, 140, 0.05) 100%)",
                  borderRadius: { xs: "6px", md: "8px" },
                  border: `1px solid ${feature.color}20`,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: { xs: "3px", md: "4px" },
                  transition: "all 0.3s ease",
                  textAlign: "center",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: `0 4px 12px ${feature.color}20`,
                  },
                }}
              >
                <Box
                  sx={{
                    color: feature.color,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {feature.icon}
                </Box>
                <Box sx={{ width: "100%" }}>
                  <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: { xs: "7px", md: "8px" }, fontWeight: 700, color: "#1e293b", lineHeight: 1.2 }}>
                    {feature.title}
                  </Typography>
                  <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: { xs: "6px", md: "7px" }, color: "#64748b", lineHeight: 1.2 }}>
                    {feature.desc}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </motion.div>
      </motion.div>
    </Box>
  );
};

export default VizApp3DCard;
